import { useEffect } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
// Import custom CSS for improved workflow UI visibility
import './components/catalog/workflows-ui-improvements.css';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { UnifiedThemeProvider } from '@backstage/theme';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { exoDarkTheme } from './theme';
import { AwsPage } from './components/aws/AwsPage';
import { TestingPage } from './components/testing/TestingPage';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { oktaAuthApiRef } from '@backstage/core-plugin-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';

const app = createApp({
  apis,
  themes: [
    {
      id: 'exo-dark',
      title: 'Exo Dark',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={exoDarkTheme} children={children} />
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        providers={[
          {
            id: 'okta-auth-provider',
            title: 'Okta',
            message: 'Sign in with Okta',
            apiRef: oktaAuthApiRef,
          },
        ]}
      />
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="aws" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/aws" element={<AwsPage />} />
    <Route path="/testing" element={<TestingPage />} />
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
  </FlatRoutes>
);

// Adds .exo-run-btn class to run/replay IconButtons and .exo-gear-btn to settings
// IconButtons in the workflow table, then styles them via a <style> tag.
// This uses DOM manipulation because CSS :has() is unreliable across build tools.
const RunButtonStyles = () => {
  useEffect(() => {
    // Known SVG path prefixes for each MUI icon
    const RUN_PATHS = ['M12 5V1L7', 'M12 4V1L8', 'M17.65 6.35'];  // Replay, Sync, Refresh
    const GEAR_PATH = 'M19.14 12.94';  // Settings

    const style = document.createElement('style');
    style.textContent = `
      .exo-run-btn {
        background: rgba(34, 197, 94, 0.15) !important;
        border: 1px solid rgba(34, 197, 94, 0.4) !important;
        border-radius: 6px !important;
        padding: 4px 12px !important;
        transition: all 0.2s ease !important;
        position: relative !important;
      }
      .exo-run-btn:hover {
        background: rgba(34, 197, 94, 0.25) !important;
        border-color: rgba(34, 197, 94, 0.6) !important;
      }
      .exo-run-btn svg {
        color: #22c55e !important;
        font-size: 18px !important;
      }
      .exo-run-btn .exo-run-label {
        color: #22c55e;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.3px;
        margin-left: 4px;
      }
      .exo-gear-btn svg {
        color: #6366f1 !important;
        cursor: pointer !important;
        transition: transform 0.2s ease !important;
      }
      .exo-gear-btn:hover svg {
        transform: rotate(45deg) !important;
      }
    `;
    document.head.appendChild(style);

    function tagButtons() {
      const tbody = document.querySelectorAll('.MuiTableBody-root');
      tbody.forEach(tb => {
        const paths = tb.querySelectorAll('path');
        paths.forEach(p => {
          const d = p.getAttribute('d') || '';
          const btn = p.closest('.MuiIconButton-root');
          if (!btn) return;

          if (RUN_PATHS.some(prefix => d.startsWith(prefix))) {
            if (!btn.classList.contains('exo-run-btn')) {
              btn.classList.add('exo-run-btn');
              // Add "Run" text label if not already present
              if (!btn.querySelector('.exo-run-label')) {
                const label = document.createElement('span');
                label.className = 'exo-run-label';
                label.textContent = 'Run';
                btn.appendChild(label);
              }
            }
          } else if (d.startsWith(GEAR_PATH)) {
            if (!btn.classList.contains('exo-gear-btn')) {
              btn.classList.add('exo-gear-btn');
            }
          }
        });
      });
    }

    // Run once immediately and then observe for DOM changes
    tagButtons();
    const observer = new MutationObserver(tagButtons);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      document.head.removeChild(style);
    };
  }, []);
  return null;
};

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <RunButtonStyles />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
